import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  Typography,
  InputAdornment,
  CircularProgress,
  Grid,
  Pagination,
  TableSortLabel,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import {
  Close as CloseIcon,
  Search as SearchIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import axiosInstance from '../../../utils/axios';

interface RelatedIncomingDocumentsProps {
  open: boolean;
  onClose: () => void;
  documentId?: number;
  onSelectDocuments?: (selectedDocs: Array<{
    id_incoming: string;
    so_ky_hieu_den: string;
    flyID_den: string;
  }>) => void;
  initialSelectedDocs?: Array<{
    id_incoming: string;
    so_ky_hieu_den: string;
    flyID_den: string;
  }>;
}

interface IncomingDocument {
  id: number;
  document_number: string;
  document_code: string;
  description: string;
  document_date: string;
  sender: string;
  docs_catalog_name: string;
  received_date: string;
  status: string;
  isSelected?: boolean;
  FlyID?: number;
}

// Định nghĩa kiểu sắp xếp
type Order = 'asc' | 'desc';

interface SortConfig {
  field: string;
  order: Order;
}

const RelatedIncomingDocuments: React.FC<RelatedIncomingDocumentsProps> = ({
  open,
  onClose,
  documentId,
  onSelectDocuments,
  initialSelectedDocs = []
}) => {
  const containerRef = React.useRef<HTMLElement | null>(null);
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [documents, setDocuments] = useState<IncomingDocument[]>([]);
  const [selectedDocuments, setSelectedDocuments] = useState<number[]>([]);
  const [allSelectedDocuments, setAllSelectedDocuments] = useState<IncomingDocument[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  
  // State cho phân trang
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  
  // State để lưu trữ kết quả tìm kiếm văn bản đã chọn
  const [searchResults, setSearchResults] = useState<IncomingDocument[]>([]);
  const [isSearchMode, setIsSearchMode] = useState(false);
  
  // State cho sắp xếp
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    field: 'received_date',
    order: 'desc'
  });
  
  // State cho CheckFlyID - 'all' | 'true' | 'false'
  const [checkFlyID, setCheckFlyID] = useState<'all' | 'true' | 'false'>('true');

  // Hàm tìm kiếm văn bản đến
  const searchDocuments = async () => {
    // Reset chế độ tìm kiếm văn bản đã chọn
    setIsSearchMode(false);
    setSearchResults([]);
    // Tìm kiếm với từ khóa trống cũng được chấp nhận
    fetchDocuments(1);
  };

  // Hàm tìm kiếm văn bản đã chọn
  const searchSelectedDocuments = async () => {
    if (allSelectedDocuments.length === 0) {
      enqueueSnackbar('Chưa có văn bản nào được chọn', { variant: 'info' });
      return;
    }

    setSearchLoading(true);
    try {
      // Lấy danh sách số ký hiệu của các văn bản đã chọn
      const selectedDocumentCodes = allSelectedDocuments
        .map(doc => doc.document_code)
        .filter(code => code && code.trim() !== '');

      if (selectedDocumentCodes.length === 0) {
        enqueueSnackbar('Không có số ký hiệu để tìm kiếm', { variant: 'warning' });
        return;
      }

      // Gọi API cho từng số ký hiệu và gộp kết quả
      const allResults: IncomingDocument[] = [];
      
      for (const documentCode of selectedDocumentCodes) {
        try {
          const params: any = {
            keyword: documentCode,
            page: 1,
            page_size: 100 // Lấy nhiều hơn để đảm bảo không bỏ sót
          };
          
          // Chỉ thêm CheckFlyID nếu không phải 'all'
          if (checkFlyID !== 'all') {
            params.CheckFlyID = checkFlyID === 'true';
          }
          
          const response = await axiosInstance.get('/xlvb/incoming-documents/danhsach-theo-flyid', { params });

          if (response.data.code === 0) {
            const fetchedDocs = response.data.data.documents || [];
            // Chỉ lấy những văn bản có số ký hiệu khớp chính xác
            const exactMatches = fetchedDocs.filter((doc: IncomingDocument) => 
              doc.document_code === documentCode
            );
            allResults.push(...exactMatches);
          }
        } catch (error) {

        }
      }

      // Loại bỏ trùng lặp dựa trên ID
      const uniqueResults = allResults.filter((doc, index, self) => 
        index === self.findIndex(d => d.id === doc.id)
      );

      // Sắp xếp văn bản đã chọn lên đầu
      const sortedResults = uniqueResults.sort((a: IncomingDocument, b: IncomingDocument) => {
        const aIsSelected = allSelectedDocuments.some((selectedDoc: IncomingDocument) => selectedDoc.id === a.id);
        const bIsSelected = allSelectedDocuments.some((selectedDoc: IncomingDocument) => selectedDoc.id === b.id);
        
        // Nếu cả hai đều được chọn hoặc cả hai đều không được chọn, giữ nguyên thứ tự
        if (aIsSelected === bIsSelected) {
          return 0;
        }
        
        // Văn bản đã chọn sẽ lên trên đầu
        return aIsSelected ? -1 : 1;
      });

      // Lưu trữ kết quả tìm kiếm đã sắp xếp và chuyển sang chế độ tìm kiếm
      setSearchResults(sortedResults);
      setIsSearchMode(true);
      
      // Tính toán phân trang cho kết quả tìm kiếm
      const totalResults = sortedResults.length;
      const totalPagesForSearch = Math.ceil(totalResults / pageSize);
      
      // Hiển thị trang đầu tiên của kết quả tìm kiếm
      const startIndex = 0;
      const endIndex = Math.min(pageSize, totalResults);
      const currentPageResults = sortedResults.slice(startIndex, endIndex);
      
      setDocuments(currentPageResults);
      
      // Cập nhật selectedDocuments cho trang hiện tại dựa trên allSelectedDocuments
      const currentPageIds = currentPageResults
        .filter((doc: IncomingDocument) => allSelectedDocuments.some((selectedDoc: IncomingDocument) => selectedDoc.id === doc.id))
        .map((doc: IncomingDocument) => doc.id);
      setSelectedDocuments(currentPageIds);

      // Cập nhật phân trang
      setPage(1);
      setTotalPages(totalPagesForSearch);
      setTotalRecords(totalResults);

      // Xóa trường tìm kiếm để hiển thị rõ đây là kết quả tìm kiếm văn bản đã chọn
      setSearchTerm('');
      
      enqueueSnackbar(`Đã tìm thấy ${totalResults} văn bản từ ${selectedDocumentCodes.length} số ký hiệu đã chọn`, { variant: 'success' });
      
    } catch (error) {

      enqueueSnackbar('Lỗi khi tìm kiếm văn bản đã chọn', { variant: 'error' });
    } finally {
      setSearchLoading(false);
    }
  };
  
  // Hàm lấy dữ liệu từ API
  const fetchDocuments = async (pageNumber: number = page) => {
    setSearchLoading(true);
    try {
      // Chuẩn bị params cho API
      const params: any = {
        keyword: searchTerm,
        page: pageNumber,
        page_size: pageSize
      };
      
      // Chỉ thêm CheckFlyID nếu không phải 'all'
      if (checkFlyID !== 'all') {
        params.CheckFlyID = checkFlyID === 'true';
      }
      
      // Gọi API thực tế để lấy danh sách văn bản đến
      const response = await axiosInstance.get('/xlvb/incoming-documents/danhsach-theo-flyid', { params });

      if (response.data.code === 0) {
        // API trả về mảng documents trong data.documents
        const fetchedDocs = response.data.data.documents || [];
        setDocuments(fetchedDocs);
        
        // Cập nhật selectedDocuments cho trang hiện tại dựa trên allSelectedDocuments
        const currentPageIds = fetchedDocs
          .filter((doc: IncomingDocument) => allSelectedDocuments.some((selectedDoc: IncomingDocument) => selectedDoc.id === doc.id))
          .map((doc: IncomingDocument) => doc.id);
        setSelectedDocuments(currentPageIds);
        
        // Cập nhật thông tin phân trang
        const pagination = response.data.data.pagination;
        if (pagination) {
          setTotalPages(pagination.total_pages || 1);
          setTotalRecords(pagination.total_records || 0);
          setPage(pagination.current_page || 1);
          
        }
      } else {
        setDocuments([]);
        setSelectedDocuments([]);
        enqueueSnackbar('Không tìm thấy văn bản phù hợp', { variant: 'info' });
      }
    } catch (error) {

      enqueueSnackbar('Lỗi khi tìm kiếm văn bản', { variant: 'error' });
      setDocuments([]);
      setSelectedDocuments([]);
    } finally {
      setSearchLoading(false);
    }
  };

  // Xử lý khi nhấn Enter trong ô tìm kiếm
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      searchDocuments();
    }
  };

  // Xử lý chọn/bỏ chọn văn bản
  const handleToggleDocument = (id: number) => {
    setSelectedDocuments(prev => {
      if (prev.includes(id)) {
        // Bỏ chọn văn bản
        const newSelected = prev.filter(docId => docId !== id);
        // Cập nhật allSelectedDocuments - xóa văn bản khỏi danh sách đã chọn
        setAllSelectedDocuments(prevAll => prevAll.filter(doc => doc.id !== id));
        return newSelected;
      } else {
        // Chọn văn bản
        const newSelected = [...prev, id];
        // Thêm văn bản vào allSelectedDocuments
        const docToAdd = documents.find(doc => doc.id === id);
        if (docToAdd) {
          setAllSelectedDocuments(prevAll => [...prevAll, docToAdd]);
        }
        return newSelected;
      }
    });
  };

  // Xử lý chọn/bỏ chọn tất cả văn bản
  const handleToggleAll = () => {
    if (selectedDocuments.length === documents.length) {
      // Bỏ chọn tất cả văn bản trong trang hiện tại
      setSelectedDocuments([]);
      // Xóa tất cả văn bản trong trang hiện tại khỏi allSelectedDocuments
      const currentPageIds = documents.map(doc => doc.id);
      setAllSelectedDocuments(prevAll => prevAll.filter(doc => !currentPageIds.includes(doc.id)));
    } else {
      // Chọn tất cả văn bản trong trang hiện tại
      const currentPageIds = documents.map(doc => doc.id);
      setSelectedDocuments(currentPageIds);
      // Thêm tất cả văn bản trong trang hiện tại vào allSelectedDocuments (tránh trùng lặp)
      setAllSelectedDocuments(prevAll => {
        const newDocs = documents.filter(doc => !prevAll.some(existingDoc => existingDoc.id === doc.id));
        return [...prevAll, ...newDocs];
      });
    }
  };

  // Xử lý thêm văn bản đến liên quan
  const handleAddRelatedDocuments = async () => {
    setLoading(true);
    try {
      // Sử dụng allSelectedDocuments thay vì documents.filter
      const relatedDocsData = allSelectedDocuments.map(doc => ({
        id_incoming: doc.id.toString(),
        so_ky_hieu_den: doc.document_code || '',  // Số đến
        flyID_den: doc.FlyID ? doc.FlyID.toString() : ''
      }));
      
      
      // Luôn sử dụng callback onSelectDocuments để lưu trữ tạm thời
      // Việc cập nhật vào database sẽ được thực hiện khi submit form chính
      if (onSelectDocuments) {
        onSelectDocuments(relatedDocsData);
        if (allSelectedDocuments.length === 0) {
          enqueueSnackbar('Đã xóa tất cả văn bản đến liên quan', { variant: 'info' });
        } else {
          enqueueSnackbar(`Đã chọn ${allSelectedDocuments.length} văn bản đến liên quan`, { variant: 'success' });
        }
        onClose();
      } else {
        enqueueSnackbar('Không có callback để lưu trữ văn bản liên quan', { variant: 'error' });
      }
    } catch (error) {

      enqueueSnackbar(`Lỗi khi thêm văn bản liên quan: ${error instanceof Error ? error.message : 'Lỗi không xác định'}`, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Hàm xử lý thay đổi trang
  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
    if (isSearchMode) {
      // Phân trang cho kết quả tìm kiếm văn bản đã chọn
      const startIndex = (value - 1) * pageSize;
      const endIndex = Math.min(startIndex + pageSize, searchResults.length);
      const currentPageResults = searchResults.slice(startIndex, endIndex);
      
      setDocuments(currentPageResults);
      
      // Cập nhật selectedDocuments cho trang hiện tại
      const currentPageIds = currentPageResults
        .filter((doc: IncomingDocument) => allSelectedDocuments.some((selectedDoc: IncomingDocument) => selectedDoc.id === doc.id))
        .map((doc: IncomingDocument) => doc.id);
      setSelectedDocuments(currentPageIds);
    } else {
      // Phân trang cho tìm kiếm thường
      fetchDocuments(value);
    }
  };
  
  // Hàm xử lý thay đổi sắp xếp
  const handleSortChange = (field: string) => {
    const isAsc = sortConfig.field === field && sortConfig.order === 'asc';
    const newOrder = isAsc ? 'desc' : 'asc';
    setSortConfig({
      field,
      order: newOrder
    });
    // Gọi lại API với thông tin sắp xếp mới
    fetchDocuments(1);
  };
  
  // Khởi tạo selectedDocuments từ initialSelectedDocs
  useEffect(() => {
    if (initialSelectedDocs && initialSelectedDocs.length > 0) {
      // Lưu trữ ID của các văn bản đã chọn trước đó
      const initialSelectedIds = initialSelectedDocs.map(doc => parseInt(doc.id_incoming));
      setSelectedDocuments(initialSelectedIds);
      
      // Tạo mock documents từ initialSelectedDocs để lưu vào allSelectedDocuments
      const mockDocuments = initialSelectedDocs.map(doc => ({
        id: parseInt(doc.id_incoming),
        document_number: '',
        document_code: doc.so_ky_hieu_den,
        description: '',
        document_date: '',
        sender: '',
        docs_catalog_name: '',
        received_date: '',
        status: '',
        FlyID: parseInt(doc.flyID_den) || undefined
      }));
      setAllSelectedDocuments(mockDocuments);
    }
  }, [initialSelectedDocs]);

  // Reset state khi đóng modal và tải dữ liệu ban đầu khi mở modal
  useEffect(() => {
    if (!open) {
      setSearchTerm('');
      setDocuments([]);
      setIsSearchMode(false);
      setSearchResults([]);
      // Không reset selectedDocuments và allSelectedDocuments ở đây để giữ lại lựa chọn trước đó
    } else {
      // Tải dữ liệu ban đầu khi mở modal
      fetchDocuments(1);
      // Delay một chút để đảm bảo dialog đã render hoàn toàn
      setTimeout(() => {
        const searchInput = document.querySelector('#search-input-field') as HTMLInputElement;
        if (searchInput) {
          searchInput.focus();
        }
      }, 100);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth
      disableEnforceFocus
      disablePortal={false}
      keepMounted={false}
      disableScrollLock
      sx={{ 
        zIndex: 1400, // Đảm bảo hiển thị trên modal chính (thường có z-index 1300)
        '& .MuiDialog-paper': {
          boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.15)',
          position: 'relative',
          overflow: 'visible'
        },
        '& .MuiBackdrop-root': {
          backgroundColor: 'rgba(0, 0, 0, 0.3)'
        }
      }}>
      <DialogTitle sx={{ bgcolor: '#2e7d32', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        Danh sách văn bản
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ color: 'white' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        {/* Thanh công cụ tìm kiếm */}
        <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderBottom: '1px solid #ddd', position: 'relative', zIndex: 10 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={5}>
              <TextField
                id="search-input-field"
                size="small"
                variant="outlined"
                placeholder="Nhập từ khóa..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={handleKeyPress}
                fullWidth
                autoFocus={false}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  sx: {
                    bgcolor: 'white',
                    '&:hover': {
                      bgcolor: 'white'
                    }
                  }
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: 'white'
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography variant="caption" sx={{ fontWeight: 500, color: '#666' }}>
                  Lọc FlyID
                </Typography>
                <ToggleButtonGroup
                  value={checkFlyID}
                  exclusive
                  onChange={(e, newValue) => {
                    if (newValue !== null) {
                      setCheckFlyID(newValue);
                    }
                  }}
                  size="small"
                  sx={{ 
                    '& .MuiToggleButton-root': {
                      px: 2,
                      py: 0.5,
                      fontSize: '0.75rem',
                      textTransform: 'none',
                      border: '1px solid #ddd',
                      '&:hover': {
                        bgcolor: '#f5f5f5'
                      },
                      '&.Mui-selected': {
                        bgcolor: '#2e7d32',
                        color: 'white',
                        '&:hover': {
                          bgcolor: '#1b5e20'
                        }
                      }
                    }
                  }}
                >
                  <ToggleButton value="all">
                    Tất cả
                  </ToggleButton>
                  <ToggleButton value="true">
                    Có FlyID
                  </ToggleButton>
                  <ToggleButton value="false">
                    Không có
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
            </Grid>
            <Grid item xs={12} sm={4} sx={{ display: 'flex', gap: 1 }}>
              <Button 
                variant="contained" 
                size="small" 
                onClick={searchDocuments}
                disabled={searchLoading}
                startIcon={searchLoading ? <CircularProgress size={16} /> : <SearchIcon />}
                sx={{ bgcolor: '#2e7d32' }}
              >
                Tìm kiếm
              </Button>
              <Button 
                variant="outlined" 
                size="small" 
                onClick={searchSelectedDocuments}
                disabled={allSelectedDocuments.length === 0}
                sx={{ 
                  borderColor: '#2e7d32', 
                  color: '#2e7d32',
                  '&:hover': {
                    borderColor: '#1b5e20',
                    backgroundColor: '#f1f8e9'
                  }
                }}
              >
                Tìm đã chọn ({allSelectedDocuments.length})
              </Button>
            </Grid>
          </Grid>
        </Box>

        {/* Bảng dữ liệu */}
        <Box sx={{ p: 0 }}>
          <TableContainer component={Paper} variant="outlined" sx={{ boxShadow: 'none', border: 'none' }}>
            <Table size="small">
              <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                <TableRow>
                  <TableCell padding="checkbox" sx={{ borderBottom: '1px solid #ddd' }}>
                    <Checkbox
                      indeterminate={selectedDocuments.length > 0 && selectedDocuments.length < documents.length}
                      checked={documents.length > 0 && selectedDocuments.length === documents.length}
                      onChange={handleToggleAll}
                    />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', borderBottom: '1px solid #ddd' }}>
                    <TableSortLabel
                      active={sortConfig.field === 'FlyID'}
                      direction={sortConfig.field === 'FlyID' ? sortConfig.order : 'asc'}
                      onClick={() => handleSortChange('FlyID')}
                    >
                      FlyID
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', borderBottom: '1px solid #ddd' }}>
                    <TableSortLabel
                      active={sortConfig.field === 'description'}
                      direction={sortConfig.field === 'description' ? sortConfig.order : 'asc'}
                      onClick={() => handleSortChange('description')}
                    >
                      Trích yếu
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', borderBottom: '1px solid #ddd' }}>
                    <TableSortLabel
                      active={sortConfig.field === 'document_code'}
                      direction={sortConfig.field === 'document_code' ? sortConfig.order : 'asc'}
                      onClick={() => handleSortChange('document_code')}
                    >
                      Số ký hiệu
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', borderBottom: '1px solid #ddd' }}>
                    <TableSortLabel
                      active={sortConfig.field === 'document_number'}
                      direction={sortConfig.field === 'document_number' ? sortConfig.order : 'asc'}
                      onClick={() => handleSortChange('document_number')}
                    >
                      Số đến
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', borderBottom: '1px solid #ddd' }}>
                    <TableSortLabel
                      active={sortConfig.field === 'received_date'}
                      direction={sortConfig.field === 'received_date' ? sortConfig.order : 'asc'}
                      onClick={() => handleSortChange('received_date')}
                    >
                      Ngày đến
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', borderBottom: '1px solid #ddd' }}>
                    <TableSortLabel
                      active={sortConfig.field === 'sender'}
                      direction={sortConfig.field === 'sender' ? sortConfig.order : 'asc'}
                      onClick={() => handleSortChange('sender')}
                    >
                      Đơn vị gửi
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {documents.length > 0 ? (
                  documents.map((doc) => (
                    <TableRow key={doc.id} hover>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedDocuments.includes(doc.id)}
                          onChange={() => handleToggleDocument(doc.id)}
                        />
                      </TableCell>
                      <TableCell>{doc.FlyID || '-'}</TableCell>
                      <TableCell>{doc.description || '-'}</TableCell>
                      <TableCell>{doc.document_code || '-'}</TableCell>
                      <TableCell>{doc.document_number || '-'}</TableCell>
                      <TableCell>{doc.received_date || '-'}</TableCell>
                      <TableCell>{doc.sender || '-'}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Box sx={{ py: 3 }}>
                        <Typography variant="body1" color="text.secondary">
                          Không tìm thấy văn bản phù hợp
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
        
        {/* Thông tin phân trang */}
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid #ddd' }}>
          <Typography variant="body2" color="text.secondary">
            {totalRecords > 0 
              ? `Hiển thị ${(page - 1) * pageSize + 1}-${Math.min(page * pageSize, totalRecords)} của ${totalRecords} kết quả` 
              : 'Không có kết quả'}
          </Typography>
          {totalPages > 1 && (
            <Pagination 
              count={totalPages} 
              page={page} 
              onChange={handlePageChange} 
              color="primary" 
              size="small"
              showFirstButton
              showLastButton
            />
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2, borderTop: '1px solid #ddd' }}>
        <Button onClick={onClose} variant="outlined">Đóng</Button>
        <Button
          variant="contained"
          onClick={handleAddRelatedDocuments}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : <AddIcon />}
          sx={{ bgcolor: '#2e7d32' }}
        >
          Chọn ({allSelectedDocuments.length})
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RelatedIncomingDocuments;